import { useQuery } from '@tanstack/react-query';
import { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { useScrollSpy } from '@/hooks/useScrollSpy';
import { trpc } from '@/lib/trpc';
import { usePickupDateTimeStore } from '@/stores/pickup-datetime-store';

import Footer from '../features/home/components/Footer';
import Header from '../features/home/components/Header';
import MainSection from '../features/home/components/MainSection';
import MenuList from '../features/home/components/MenuList';
import TabBar from '../features/home/components/TabBar';

export default function HomePage() {
  const navigate = useNavigate();
  const { pickupDate, pickupTime } = usePickupDateTimeStore();

  const [activeTab, setActiveTab] = useState(''); // 기본값을 빈 문자열로 설정
  const isUserScrolling = useRef(false); // 사용자가 탭을 클릭해서 스크롤 중인지 확인
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null); // 스크롤 완료 감지용 타이머

  // 픽업 날짜/시간이 선택되지 않았다면 캘린더 페이지로 이동
  useEffect(() => {
    if (!pickupDate || !pickupTime) {
      navigate('/', { replace: true });
    }
  }, [pickupDate, pickupTime, navigate]);

  const { data: menuCategories, isLoading, isError, error, refetch } = useQuery(trpc.menu.grouped.queryOptions());
  // 사용 가능한 카테고리 목록
  const availableCategories = useMemo(() => {
    return menuCategories?.map(category => category.name) || [];
  }, [menuCategories]);

  // 스크롤 위치에 따른 현재 섹션 감지
  const currentSection = useScrollSpy(availableCategories);

  // 첫 번째 사용 가능한 카테고리를 기본 탭으로 설정
  useEffect(() => {
    if (availableCategories.length > 0 && !activeTab) {
      setActiveTab(availableCategories[0]);
    }
  }, [availableCategories, activeTab]);

  // 스크롤에 의한 섹션 변경 시 탭 업데이트 (사용자가 탭 클릭 중이 아닐 때만)
  useEffect(() => {
    if (currentSection && currentSection !== activeTab && !isUserScrolling.current) {
      setActiveTab(currentSection);
    }
  }, [currentSection, activeTab]);

  // 스크롤 완료 감지를 위한 이벤트 리스너
  useEffect(() => {
    const handleScroll = () => {
      // 이미 스크롤 중이 아니라면 무시
      if (!isUserScrolling.current) return;

      // 이전 타이머가 있다면 클리어
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }

      // 스크롤이 멈춘 후 100ms 후에 플래그 해제
      scrollTimeoutRef.current = setTimeout(() => {
        isUserScrolling.current = false;
        scrollTimeoutRef.current = null;
      }, 100);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);

    // 사용자 스크롤 플래그 설정
    isUserScrolling.current = true;

    // 기존 타이머가 있다면 클리어
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
      scrollTimeoutRef.current = null;
    }
  };

  // 메뉴 카테고리별로 그룹화된 데이터는 이미 구조화되어 있음

  // 로딩 상태
  if (isLoading) {
    return (
      <div className="min-h-screen bg-white w-full">
        <Header />
        <MainSection />
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto mb-4"></div>
            <p className="text-gray-600">메뉴를 불러오는 중...</p>
          </div>
        </div>
      </div>
    );
  }

  // 에러 상태
  if (isError) {
    return (
      <div className="min-h-screen bg-white w-full">
        <Header />
        <MainSection />
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <p className="text-red-600 mb-4">
              {(error instanceof Error && error.message) || '메뉴 데이터를 불러오는데 실패했습니다.'}
            </p>
            <button
              onClick={() => refetch()}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              다시 시도
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white w-full">
      {/* Header - Sticky */}
      <Header />

      {/* Main Image Section */}
      <MainSection />

      {/* Tab Bar - Sticky */}
      <TabBar activeTab={activeTab} onTabChange={handleTabChange} categories={availableCategories} />

      {/* Menu List - Scrollable */}
      <MenuList menuCategories={menuCategories || []} />

      {/* Footer */}
      <Footer />
    </div>
  );
}
